import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import { useAuth } from "../context/AuthContext";
import React, { Fragment, useEffect, useState } from "react";
import { AxisChartColours } from "../util/AxisChartColours";
import "chart.js/auto";
import { Bar } from "react-chartjs-2";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import { Container } from "react-bootstrap";
import DropDownTimesheetWeekSelection from "./DropDownTimesheetWeekSelection";
import { DropDownMonthSelection } from "./DropDownMonthSelection";
import { DateTime } from "luxon";
import AxisSpinner from "./AxisSpinner";
import { BsDownload, BsArrowClockwise } from "react-icons/bs";

export const StatNurseBookedShifts = (prop) => {
  const { authAxios } = useAuth();
  const now = DateTime.now().set({
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const [loading, setLoading] = useState(false);
  const [tallyData, setTallyData] = useState(null);
  const [chartTally, setChartTally] = useState(null);
  const [chartTallyOptions, setChartTallyOptions] = useState(null);
  const [selectCategoryFilter, setSelectCategoryFilter] = useState("Year to Date");
  const [selectWeekendingFilter, setSelectWeekendingFilter] = useState(null);
  const [selectMonthFilter, setSelectMonthFilter] = useState(null);

  useEffect(() => {
    setChartTally(null);
    setChartTallyOptions(null);
    setTallyData(null);
    fetch();
  }, [selectCategoryFilter]);

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    if (tallyData != null) {
      var backgroundColours = AxisChartColours(tallyData.labels.length);
      setChartTally({
        labels: tallyData.labels,
        datasets: [
          {
            label: "Shifts Booked",
            data: tallyData.data,
            backgroundColor: backgroundColours,
            hoverOffset: 50,
          },
        ],
      });
      setChartTallyOptions({
        plugins: {
          tooltip: {
            titleFont: {
              size: 18,
            },
            bodyFont: {
              size: 14,
              weight: "bold",
            },
            backgroundColor: "#00005F",
            borderColor: "#EEEFEF",
            borderWidth: 2,
          },
          legend: {
            labels: {
              font: {
                size: 14,
                weight: "bold",
              },
              color: "#00005F",
            },
          },
        },
      });
    }
  }, [tallyData]);

  useEffect(() => {
    fetch();
  }, [selectWeekendingFilter, selectMonthFilter]);

  const fetch = async () => {
    var params = {};
    if (selectCategoryFilter === "Year to Date") {
      params = {
        dateStart: now.startOf("year").toISODate(),
        dateEnd: now.toISODate(),
      };
    }
    if (selectCategoryFilter === "Week") {
      if (selectWeekendingFilter === null) return;
      params = { timesheet_week_id: selectWeekendingFilter?.id };
    }
    if (selectCategoryFilter === "Month") {
      if (selectMonthFilter === null) return;
      params = {
        dateStart: selectMonthFilter.toISODate(),
        dateEnd: selectMonthFilter.plus({ months: 1 }).toISODate(),
      };
    }

    setLoading(true);

    const config = {
      headers: {
        "content-type": "application/json",
      },
      params: params,
    };
    try {
      const response = await authAxios().get(
        "/api/backoffice/stats/nurse_booked",
        config,
      );

      var labels = [];
      var data = [];
      response.data.forEach((item) => {
        if (item?.tally !== 0 && labels.length < 20) {
          labels.push(
            item?.approver?.surname + ", " + item?.approver?.forename,
          );
          data.push(item?.tally);
        }
      });
      setTallyData({
        labels: labels,
        data: data,
      });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.log("Error catch");
      console.log(e);
    }
  };

  const getTotalTallyDisplay = (totalTally) => {
    if (totalTally === null || totalTally === undefined) return;
    return (
      <Fragment>
        {totalTally.labels.map((item, i) => (
          <li key={i} className={"h4 pb-1"}>
            {item}: {totalTally.data[i]}
          </li>
        ))}
      </Fragment>
    );
  };

  const displayChartTotalTally = (chartTotalTally) => {
    if (chartTotalTally === null || chartTotalTally === undefined) return;
    return (
      <Bar
        className={"w-100"}
        data={chartTotalTally}
        options={chartTallyOptions}
      />
    );
  };

  const getCategoryFilterButton = (name) => {
    return (
      <Button
        className={"w-100 font-weight-bold mb-2"}
        onClick={() => {
          setSelectCategoryFilter(name);
        }}
        variant={selectCategoryFilter === name ? "offblack" : "secondary"}
      >
        {name}
      </Button>
    );
  };

  const getCategoryFilters = () => {
    return (
      <Fragment>
        <Col>{getCategoryFilterButton("Year to Date")}</Col>
        <Col>{getCategoryFilterButton("Month")}</Col>
        <Col>{getCategoryFilterButton("Week")}</Col>
      </Fragment>
    );
  };

  const getSubCategoryFilters = () => {
    if (selectCategoryFilter === "Week")
      return (
        <Container fluid>
          <DropDownTimesheetWeekSelection
            onSelected={setSelectWeekendingFilter}
            defaultSelect={selectWeekendingFilter}
            useCurrentTimesheetWeekending={true}
          />
        </Container>
      );
    if (selectCategoryFilter === "Month")
      return (
        <Container fluid>
          <DropDownMonthSelection
            startFromMonthLuxtonDatetime={now}
            onSelected={setSelectMonthFilter}
            defaultSelect={selectMonthFilter}
            useStartDate={true}
          />
        </Container>
      );
  };
  
  const handleRefresh = () => {
    fetch();
  };
  
  const handleExportData = () => {
    if (!tallyData || !tallyData.labels || !tallyData.data) {
      alert("No data available to export");
      return;
    }
    
    try {
      // Create CSV content
      let csvContent = "Nurse,Shifts Booked\n";
      
      // Add each nurse and their booked shifts count
      tallyData.labels.forEach((label, index) => {
        csvContent += `"${label}",${tallyData.data[index]}\n`;
      });
      
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      
      // Create a link element to trigger the download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      // Set link properties and include the category in filename
      const category = selectCategoryFilter.toLowerCase();
      link.setAttribute("href", url);
      link.setAttribute("download", `nurse_booked_shifts_${category}_${new Date().toISOString().split('T')[0]}.csv`);
      
      // Append to document, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Failed to export data. See console for details.");
    }
  };

  const spinnerOrData = () => {
    if (loading) return <AxisSpinner loading={loading} />;
    return (
      <Fragment>
        <div className="d-flex justify-content-between mb-3 align-items-center">
          <div>
            {getSubCategoryFilters()}
          </div>
          <div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="me-2"
              onClick={handleRefresh}
            >
              <BsArrowClockwise className="me-1" /> Refresh
            </Button>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleExportData}
            >
              <BsDownload className="me-1" /> Export
            </Button>
          </div>
        </div>
        <Row xs={1} md={2} className={"mt-4"}>
          <Col className={"justify-content-center align-items-center"}>
            <Card.Text as={"ol"} className={"text-axis-offblack h3"}>
              {getTotalTallyDisplay(tallyData)}
            </Card.Text>
          </Col>
          <Col className={"h-50 w-50"}>
            {displayChartTotalTally(chartTally)}
          </Col>
        </Row>
      </Fragment>
    );
  };

  if (tallyData === null && selectCategoryFilter === "Year to Date") return null;

  return (
    <Fragment>
      <Col>
        <Card>
          <Card.Body style={{ minHeight: "300px" }}>
            <Card.Title className={"text-axis-offblack"}>
              Nurse Booked Shifts
            </Card.Title>
            <Row>{getCategoryFilters()}</Row>
            {spinnerOrData()}
          </Card.Body>
        </Card>
      </Col>
    </Fragment>
  );
};
